import React, { FC, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../../api';
import './email-share-modal.scss';

interface User {
  id: string;
  name: string;
  currentPermission?: 'NONE' | 'READ' | 'WRITE';
}

interface EmailShareModalProps {
  emailId: string;
  isOwner: boolean;
  onClose: () => void;
}

const EmailShareModal: FC<EmailShareModalProps> = ({
  emailId,
  isOwner,
  onClose,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (isOwner) {
      fetchCompanyUsersWithAccess();
    }
  }, [isOwner, emailId]);

  const fetchCompanyUsersWithAccess = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();

      // Get all company users
      const usersResponse = await api.get(`/agents/users/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Get current email shares
      const sharesResponse = await api.get(
        `/agent-email-storage/${emailId}/shares`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (usersResponse.data.success) {
        const users = usersResponse.data.users;
        const shares = sharesResponse.data.shares || [];

        // Map current permissions to users
        const usersWithPermissions = users.map((user: any) => {
          const share = shares.find((s: any) => s.userId === user.id);
          return {
            id: user.id,
            name: user.name,
            currentPermission: share ? share.permission : 'NONE',
          };
        });

        setCompanyUsers(usersWithPermissions);
      }
    } catch (error) {
      console.error('Error fetching company users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (userId: string, permission: string) => {
    const updatedUsers = companyUsers.map((user) =>
      user.id === userId
        ? { ...user, currentPermission: permission as any }
        : user,
    );
    setCompanyUsers(updatedUsers);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = await getAccessTokenSilently();

      // Build grants and revocations
      const grants = companyUsers
        .filter(
          (user) => user.currentPermission && user.currentPermission !== 'NONE',
        )
        .map((user) => ({
          userId: user.id,
          permission: user.currentPermission!,
        }));

      const revocations = companyUsers
        .filter((user) => user.currentPermission === 'NONE')
        .map((user) => user.id);

      await api.put(
        `/agent-email-storage/${emailId}/shares`,
        { grants, revocations },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onClose();
    } catch (error) {
      console.error('Error updating email shares:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='email-share-modal'>
      {!isOwner ? (
        // Non-owner view
        <>
          <h1>Shared with you</h1>
          <p>You have access to view this email.</p>
        </>
      ) : (
        // Owner view
        <>
          <h1>Share email</h1>
          <p>
            Users you share with will be able to view or edit this draft based
            on the permission level you grant.
          </p>

          <div className='share-area'>
            {loading ? (
              <div className='loading'>Loading users...</div>
            ) : (
              <div className='users-list'>
                {companyUsers.map((user) => (
                  <div key={user.id} className='user-row'>
                    <div className='user-info'>
                      <div className='user-name'>{user.name}</div>
                    </div>
                    <select
                      value={user.currentPermission || 'NONE'}
                      onChange={(e) =>
                        handlePermissionChange(user.id, e.target.value)
                      }
                      className='permission-select'
                    >
                      <option value='NONE'>None</option>
                      <option value='READ'>Read</option>
                      <option value='WRITE'>Write</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className='modal-actions'>
            <button onClick={onClose} className='cancel-btn' disabled={saving}>
              Cancel
            </button>
            <button onClick={handleSave} className='save-btn' disabled={saving}>
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailShareModal;
